import { formatUsd } from "./money.js";

// Safe number conversion - prevents NaN from breaking calculations
export const safeNum = (val) => { const n = Number(val); return (isNaN(n) || !isFinite(n)) ? 0 : n; };
// Parse "YYYY-MM-DD" as LOCAL date (not UTC) to avoid timezone day-shift
export function parseLocalDate(str) {
  if (!str) return new Date(NaN);
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d || 1);
}
// Whole calendar days from today (the viewer's date) to a date-only value
// ("YYYY-MM-DD"): 0 = today, 1 = tomorrow, -1 = yesterday. Counting from
// the clock instead (new Date("YYYY-MM-DD") is UTC midnight) put
// countdowns a day off every evening and around DST (audit LIFE-19).
// Not a date -> NaN, so every "<= N days" test is false, as before.
export function daysUntil(dateStr, from = new Date()) {
  const s = String(dateStr || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return NaN;
  const [y, m, d] = s.split("-").map(Number);
  return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(from.getFullYear(), from.getMonth(), from.getDate())) / 86400000);
}

export function formatLocalDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// ---------------------------------------------------------------------------
// DISPLAY dates. formatLocalDate above is the STORAGE format (YYYY-MM-DD, what
// Postgres and <input type="date"> want) and must not change. Everything the
// user reads goes through fmtDate instead, so the app shows one US format
// rather than the three it drifted into: raw YYYY-MM-DD straight from the
// column, toLocaleDateString() with no locale (whatever the browser feels
// like), and toDateString()'s "Mon Sep 01 2026".
//
// A date-only string is parsed LOCAL, never through new Date("2026-09-16"),
// which the spec reads as UTC and renders as the 15th west of Greenwich.
function toDate(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === "number") { const d = new Date(value); return isNaN(d.getTime()) ? null : d; }
  const str = String(value).trim();
  if (!str) return null;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
  if (dateOnly) {
    const y = +dateOnly[1], mo = +dateOnly[2], day = +dateOnly[3];
    const d = new Date(y, mo - 1, day);
    // the Date constructor rolls overflow forward silently, so "2026-13-45"
    // would come back as 02/14/2027 rather than being rejected
    if (isNaN(d.getTime()) || d.getMonth() !== mo - 1 || d.getDate() !== day) return null;
    return d;
  }
  // an ISO timestamp is a real instant, so let the engine localise it
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

// "09/16/2026". Returns fallback (default "") for null/blank/unparseable, so a
// missing date reads as empty rather than "Invalid Date" or "NaN/NaN/NaN".
export function fmtDate(value, fallback = "") {
  const d = toDate(value);
  if (!d) return fallback;
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${m}/${day}/${d.getFullYear()}`;
}

// "09/16/2026 3:42 PM" — for timestamps where the time of day matters
// (sync runs, audit entries, uploads).
export function fmtDateTime(value, fallback = "") {
  const d = toDate(value);
  if (!d) return fallback;
  let h = d.getHours();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${fmtDate(d)} ${h}:${String(d.getMinutes()).padStart(2, "0")} ${ampm}`;
}

// "09/2026" — statement and report period headings.
export function fmtMonthYear(value, fallback = "") {
  const d = toDate(value);
  if (!d) return fallback;
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

// Excel wants a real Date in the cell plus a US number format, so the column
// still sorts and filters as a date instead of as text.
// ---------------------------------------------------------------------------
// Co-tenants. The schema stores one tenancy as one row, so two people on one
// lease arrive as name "Jamie Mahoney & Kevin Herrington" with BOTH phone
// numbers crammed into `phone` as "(443) 793-5630, 443-591-3701" and often no
// email at all. Rather than change the schema, read the row as the several
// people it describes: contact details are personal, money is not.
//
// Contacts are paired to names BY POSITION, which is the only signal the data
// carries. One contact for two names means it belongs to nobody in particular,
// so it stays on the tenancy rather than being attributed to the first person.
const PARTY_SPLIT = /\s*(?:&|\+|\band\b|\/)\s*/i;

export function splitParties(tenant) {
  const whole = String(tenant?.name || "").trim();
  if (!whole) return [];
  const names = whole.split(PARTY_SPLIT).map(n => n.trim()).filter(Boolean);
  // "Smith & Sons LLC" is one party, not two. Two people on a lease are each
  // written as at least a first and last name, so a fragment that is a single
  // word, or that carries a company suffix, means the "&" was never a join.
  const looksLikeEntity = n => /\b(llc|l\.l\.c|inc|corp|co|ltd|lp|llp|trust|properties|management|holdings|sons|associates)\b\.?$/i.test(n);
  const isPerson = n => n.length >= 3 && /\s/.test(n) && !looksLikeEntity(n);
  if (names.length < 2 || !names.every(isPerson)) {
    return [{ name: whole, email: tenant?.email || "", phone: tenant?.phone || "" }];
  }
  const split = (v) => String(v || "").split(/[,;]+/).map(x => x.trim()).filter(Boolean);
  const emails = split(tenant?.email), phones = split(tenant?.phone);
  return names.map((name, i) => ({
    name,
    // one contact shared between several names identifies no one
    email: emails.length === names.length ? emails[i] : "",
    phone: phones.length === names.length ? phones[i] : "",
  }));
}

// Contacts that belong to the tenancy rather than to a named person: what is
// left when the per-person pairing above could not claim them.
export function sharedContacts(tenant) {
  const parties = splitParties(tenant);
  if (parties.length < 2) return { emails: [], phones: [] };
  const claimed = parties.some(p => p.email || p.phone);
  if (claimed) return { emails: [], phones: [] };
  const split = (v) => String(v || "").split(/[,;]+/).map(x => x.trim()).filter(Boolean);
  return { emails: split(tenant?.email), phones: split(tenant?.phone) };
}

export function initialsFor(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export const EXCEL_DATE_FMT = "mm/dd/yyyy";
export function excelDate(value) { return toDate(value); }

// Short random ID for references (avoids Date.now() collisions)
export function shortId() {
  const arr = new Uint8Array(6);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) crypto.getRandomValues(arr);
  else for (let i = 0; i < 6; i++) arr[i] = Math.floor(Math.random() * 256);
  // 6 bytes → 12 hex chars. Previous .slice(0, 12) was a no-op since
  // we already produce exactly that length.
  return Array.from(arr, b => b.toString(16).padStart(2, "0")).join("");
}

// The same inputs always give the same 12-hex key (two 32-bit FNV-1a passes).
// For references that must repeat when the same posting is retried, so the
// database's unique reference index refuses the second copy (audit MONEY-16).
export function stableKey(...parts) {
  const str = parts.map(p => String(p ?? "")).join("\u241f");
  let a = 0x811c9dc5, b = 0x01000193 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193) >>> 0;
    b = Math.imul(b ^ c, 0x5bd1e995) >>> 0;
  }
  return (a.toString(16).padStart(8, "0") + b.toString(16).padStart(8, "0")).slice(0, 12);
}

// Generate secure random ID (better than Date.now + Math.random)
export const CLASS_COLORS = ["#3B82F6","#10B981","#F59E0B","#EF4444","#8B5CF6","#06B6D4","#F97316","#EC4899"];
// Single source of truth for loan types across the Loans page, the property
// wizard, and portfolio loans. Previously each had its own list, so a type
// picked in one (e.g. ARM) had no matching <option> in another and the select
// silently displayed the first item ("Conventional"). Render with the stored
// value injected first so any legacy/foreign value still shows as itself.
export const LOAN_TYPES = ["Conventional","FHA","VA","USDA","ARM","DSCR","Interest-Only","Hard Money","HELOC","Commercial","Portfolio","Blanket","Other"];
// Management tier: who may run destructive / financial actions (Delete,
// Terminate, Pay, Archive, Disable; bookkeeping moved to canKeepBooks). Mirrors the DB gate in migration
// 20260925010000 (is_management_tier). office_assistant and lower are excluded.
// The DB trigger is the real enforcement; this hides the buttons so those roles
// never see an action they can't complete.
export const MANAGEMENT_ROLES = ["admin", "pm", "manager"];
export const canManage = (role) => MANAGEMENT_ROLES.includes(role);
// Bookkeeping tier: who may keep the books -- void/reverse or add a journal
// entry, delete a $0 GL account, re-open a reconciliation, set/remove the
// period lock, pay a utility bill. Every role whose page list includes
// Accounting. Mirrors is_accounting_tier (migrations 20260929080000 /
// 20260929090000: trg_je_void_gate, trg_acct_delete_gate, apl_write_accounting).
// Terminate, owner payouts, property delete etc. stay on canManage.
export const ACCOUNTING_ROLES = ["admin", "pm", "manager", "office_assistant", "accountant"];
export const canKeepBooks = (role) => ACCOUNTING_ROLES.includes(role);
export const loanTypeOptions = (current) => [...new Set([current, ...LOAN_TYPES].filter(Boolean))];
export const ALLOWED_DOC_TYPES = ["application/pdf","image/jpeg","image/png","image/gif","image/webp","application/msword","application/vnd.openxmlformats-officedocument.wordprocessingml.document","application/vnd.ms-excel","application/vnd.openxmlformats-officedocument.spreadsheetml.sheet","text/plain","text/csv"];
export const ALLOWED_DOC_EXTENSIONS = /\.(pdf|jpg|jpeg|png|gif|webp|doc|docx|xls|xlsx|txt|csv)$/i;
export function pickColor(str) {
  let hash = 0;
  for (let i = 0; i < (str || "").length; i++) hash = ((hash << 5) - hash) + str.charCodeAt(i);
  return CLASS_COLORS[Math.abs(hash) % CLASS_COLORS.length];
}
export function generateId(prefix = "") {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  let id = "";
  const arr = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
  crypto.getRandomValues(arr);
  } else {
  for (let i = 0; i < 16; i++) arr[i] = Math.floor(Math.random() * 256);
  }
  for (let i = 0; i < 16; i++) id += chars[arr[i] % chars.length];
  return (prefix ? prefix + "-" : "") + id;
}

// Format person name from parts: "Sahil A. Agarwal"
export function formatPersonName(first, mi, last) {
  const parts = [];
  if (first) parts.push(first.trim());
  if (mi) parts.push(mi.trim().charAt(0).toUpperCase() + ".");
  if (last) parts.push(last.trim());
  return parts.join(" ") || "";
}
// Build name object for DB insert/update (sets name + first_name + middle_initial + last_name)
export function buildNameFields(first, mi, last) {
  return { name: formatPersonName(first, mi, last), first_name: (first || "").trim(), middle_initial: (mi || "").trim().charAt(0).toUpperCase() || "", last_name: (last || "").trim() };
}
// Parse a single name string into parts (for backward compat)
export function parseNameParts(fullName) {
  if (!fullName) return { first_name: "", middle_initial: "", last_name: "" };
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return { first_name: parts[0], middle_initial: "", last_name: "" };
  if (parts.length === 2) return { first_name: parts[0], middle_initial: "", last_name: parts[1] };
  return { first_name: parts[0], middle_initial: parts[1].charAt(0), last_name: parts.slice(2).join(" ") };
}

// Guard: require companyId — FAIL CLOSED if missing (no silent fallback)
export function isValidEmail(e) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e).trim()); }
export function normalizeEmail(email) {
  return (email || "").toLowerCase().trim();
}
// the one currency formatter (src/utils/money.js)
export function formatCurrency(amount) {
  return formatUsd(amount);
}
// Generate a time-limited signed URL for private storage files (1 hour expiry)
// NOTE: pmError is injected via setHelperPmError to avoid circular dependency with errors.js
let _pmError = null;
export function setHelperPmError(fn) { _pmError = fn; }
// Supabase signs a URL on its own domain:
//
//   https://<project-ref>.supabase.co/storage/v1/object/sign/<bucket>/<path>?token=...
//
// which is what a tenant saw in the address bar when they opened their own
// lease. Three things wrong with that: it puts the project ref in front of
// whoever the link is forwarded to, it reads as somebody else's website on
// a document with our name on it, and the address is not ours to keep --
// every saved or shared link breaks the day the storage host changes.
//
// vercel.json rewrites /docs/* to that same signing endpoint at the EDGE, so
// this costs no serverless function -- which matters at 12 of 12 on Hobby.
// The signature still travels in ?token=, so access control is unchanged:
// this moves the hostname and nothing else.
const STORAGE_PATH = "/storage/v1/object/sign/";

export function proxiedStorageUrl(signedUrl, origin) {
  if (!signedUrl) return "";
  const here = origin || (typeof window !== "undefined" ? window.location.origin : "");
  // Only where the rewrite exists. `npm start` serves from CRA's dev server,
  // which knows nothing about vercel.json, so a proxied URL there 404s.
  if (!/^https:\/\/([a-z-]+\.)?housify365\.com$/.test(here)) return signedUrl;
  // Each host proxies its OWN storage project (vercel.json: test.housify365.com
  // -> the TEST project, everything else -> production). A signed URL from
  // the other project would fail its signature check behind the proxy, which
  // broke every document link on the test site (audit LIFE-1), so only a URL
  // whose project matches this host is rewritten.
  const wantRef = /^https:\/\/test\.housify365\.com$/.test(here) ? "vpeewlplgxthckpidhxo" : "hoymytpyaudjvsgiiibn";
  if (!signedUrl.startsWith("https://" + wantRef + ".supabase.co/")) return signedUrl;
  const i = signedUrl.indexOf(STORAGE_PATH);
  if (i === -1) return signedUrl;   // not a storage URL — hand it back untouched
  return here + "/docs/" + signedUrl.slice(i + STORAGE_PATH.length);
}


// Format phone: accepts digits, adds +1 prefix, formats as (XXX) XXX-XXXX
export function formatPhoneInput(value) {
  // Strip everything except digits and +
  let digits = value.replace(/[^\d+]/g, "");
  // If they typed +, keep the country code portion
  if (digits.startsWith("+")) {
  // International format — limit to 15 digits total (E.164 max)
  return digits.slice(0, 16);
  }
  // US format — strip to 10 digits
  digits = digits.replace(/\D/g, "");
  if (digits.length > 10) digits = digits.slice(0, 10);
  // Format as (XXX) XXX-XXXX
  if (digits.length >= 7) return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  if (digits.length >= 4) return `(${digits.slice(0,3)}) ${digits.slice(3)}`;
  if (digits.length > 0) return `(${digits}`;
  return "";
}

// Canonical list of required docs per tenant. The match keywords are tested as
// case-insensitive substrings against both document.name and document.type.
// A doc counts as satisfying a requirement if ANY of its keywords matches.
// REQUIRED_TENANT_DOCS: each entry matches by explicit doc.type first
// (authoritative — set by the upload widget's type dropdown), then falls
// back to word-boundary name regex. Previous loose substring matching
// flipped tenants to "complete" on false positives: video.pdf satisfied
// "Government-Issued ID" because "video" contains "id", and
// life_insurance_beneficiaries.pdf satisfied "Renters Insurance".
// Is this GL account a bank account?
//
// ONE definition, because the app had three and they disagreed:
//   * the balance sheet and cash flow asked subtype === "Bank" OR the name
//     contained "Checking"/"Savings"
//   * the reconciler ignored the subtype entirely and took any Asset whose
//     code started 1[0-6] -- which for one real company is ~170 accounts,
//     including 87 tenant receivables and 39 properties. You could select
//     "AR - Jasmine Morgan" and reconcile her against a bank statement.
//
// The name rule is the subtler bug and it has a live example: "Utopia
// Atlantic Checking" is a CURRENT ASSET, not a bank account. Reading the
// word "Checking" as "bank" reported it as cash on the balance sheet and
// inside the cash-flow statement's beginning and ending cash. It is at zero
// today, so nothing is visibly wrong -- which is exactly how a rule like
// this survives until the day the account carries a balance.
//
// Classification lives in the subtype. The name is what a person called it;
// the subtype is what it IS.
//
// feedAccountIds is the one honest override: an account you have actually
// linked to a bank feed is a bank account whatever its subtype says, because
// you told us so by mapping it.
export function isBankAccount(account, feedAccountIds) {
  if (!account) return false;
  if (String(account.subtype || "") === "Bank") return true;
  return !!(feedAccountIds && feedAccountIds.has && feedAccountIds.has(account.id));
}

export const REQUIRED_TENANT_DOCS = [
  { label: "Signed Lease Agreement", types: ["Lease"], nameRe: /\blease\b/i },
  { label: "Government-Issued ID", types: ["ID"], nameRe: /\b(id|license|passport|government[-_\s]?issued)\b/i },
  { label: "Renters Insurance", types: ["Insurance"], nameRe: /\b(renters?[-_\s]?insurance|rental[-_\s]?insurance)\b/i },
  // "Other" deliberately does NOT satisfy this: it is the default for
  // anything unclassified, so accepting it would mark the requirement met
  // for any tenant with a stray upload.
  { label: "Proof of Utility Transfer", types: ["Utility Transfer"], nameRe: /\b(utility|utilities)\b/i },
];

// The classifications a document can carry. The first four exist so a
// requirement above can be satisfied by SAYING what a file is, rather than
// depending on whoever named it -- a scan called "Epson_11082024113258.pdf"
// is a renter's insurance certificate and no regex will ever know that.
// Providers the app can actually PAY, not merely read.
//
// The authority is worker/portals/playbooks.js -- a portal is payable when it
// has a `pay` recipe. That file cannot be imported here: it lives outside
// src/ and the browser bundle must not reach into the worker tree. So this is
// a deliberate copy, and tests/utility-payments.test.js fails if it stops
// matching, which turns drift into a red test instead of a Pay button that
// promises a payment nothing can carry out.
//
// Aliases exist because the data is messy: production says "Washington Gas",
// "Wash Gas" and "WGL" for the same company.
export const PAYABLE_PORTALS = [
  { portal: "wssc", provider: "WSSC",
    aliases: ["wssc", "wssc water", "washington suburban sanitary commission"] },
  { portal: "washington_gas", provider: "Washington Gas",
    aliases: ["washington gas", "wash gas", "washington gas light", "wgl", "wgl gas"] },
  { portal: "pepco", provider: "Pepco",
    aliases: ["pepco", "potomac electric", "potomac electric power"] },
  // BGE is payable, but a payment needs a person to sign in once (it mails a
  // code). The button is offered; the worker refuses to sign into BGE on its
  // own and reuses a session the person created with enroll.js.
  { portal: "bge", provider: "BGE",
    aliases: ["bge", "baltimore gas and electric", "baltimore gas & electric"] },
];

// The portal key for a provider name, or null when it cannot be paid.
// Case- and whitespace-insensitive, because the stored names are neither.
export function payablePortalFor(providerName) {
  const n = String(providerName || "").trim().toLowerCase();
  if (!n) return null;
  for (const p of PAYABLE_PORTALS) {
    if (p.aliases.some(a => n === a || n.includes(a))) return p.portal;
  }
  return null;
}

export const DOC_TYPES = [
  { value: "Lease", label: "Lease" },
  { value: "ID", label: "Government-Issued ID" },
  { value: "Insurance", label: "Renters Insurance" },
  { value: "Utility Transfer", label: "Proof of Utility Transfer" },
  // Filed automatically by the portal sweep and the payment worker. They are
  // in this list because DOC_TYPES is what the Documents filter and the
  // per-property grouping are built from -- a type that is not here is a
  // folder nobody can open.
  { value: "Utility Bill", label: "Utility Bill" },
  { value: "Utility Receipt", label: "Utility Payment Receipt" },
  { value: "Inspection", label: "Inspection" },
  { value: "Maintenance", label: "Maintenance" },
  { value: "Financial", label: "Financial" },
  { value: "Notice", label: "Notice" },
  { value: "Other", label: "Other" },
];

// True if the given list of documents covers every REQUIRED_TENANT_DOCS entry.
// Is ONE requirement met by this document set? Exported because the
// checklist in the tenant panel needs the same answer per row, and kept its
// own copy of the old rule when hasAllRequiredTenantDocs was fixed -- so
// classifying a document updated doc_status while the visible checklist
// still showed it outstanding. One rule, two callers.
// Only the CLASSIFIED type counts (2026-10-13): the filename fallback let any
// "utility bill" scan named utility.pdf pass as proof of utility transfer
// (Sigma: 434 bills, 2 real proofs). Classify the file to satisfy a line.
// `nameRe` on REQUIRED_TENANT_DOCS is kept only to SUGGEST a type on upload.
export function isRequiredDocMet(docs, { types }) {
  return (docs || []).some(d => types.includes((d?.type || "").trim()));
}

export function hasAllRequiredTenantDocs(docs) {
  return REQUIRED_TENANT_DOCS.every(r => isRequiredDocMet(docs, r));
}


export function sanitizeFileName(name) {
  if (!name) return "file";
  return String(name).replace(/[^a-zA-Z0-9._-]/g, "_").slice(0, 100);
}
// ============ CSV EXPORT HELPER ============
export function exportToCSV(data, columns, filename, showToast) {
  if (!data || data.length === 0) { if (showToast) showToast("No data to export.", "error"); return; }
  const header = columns.map(c => c.label).join(",");
  const rows = data.map(row => columns.map(c => {
  let val = typeof c.key === "function" ? c.key(row) : row[c.key];
  if (val === null || val === undefined) val = "";
  val = String(val).replace(/"/g, '""');
  return val.includes(",") || val.includes('"') || val.includes("\n") ? `"${val}"` : val;
  }).join(","));
  const csv = [header, ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename + ".csv"; a.click();
  URL.revokeObjectURL(url);
}

export function buildAddress(p) {
  const parts = [p.address_line_1, p.address_line_2, p.city, (p.state && p.zip) ? p.state + " " + p.zip : p.state || p.zip].filter(Boolean);
  return parts.join(", ") || p.address || "";
}

// A blank window to write a printable page into. "noopener" in window.open's
// features makes it return null (HTML spec), so code that then writes into
// the window printed nothing at all -- the tenant ledger PDF, the owner
// statement and the eviction notice all did this. Open normally, then cut
// the link back to the app, which is what noopener was there for.
export function openPrintWindow(features = "") {
  const w = window.open("", "_blank", features);
  if (w) { try { w.opener = null; } catch (_) { /* cross-origin: nothing to cut */ } }
  return w;
}

export function escapeHtml(str) {
  if (!str) return "";
  // Backticks are benign in most HTML output contexts but can matter if
  // the escaped value flows back into a server-side template literal
  // interpolation. Cheap to include.
  return String(str).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;").replace(/'/g,"&#039;").replace(/`/g,"&#096;");
}
// Sanitize user input for Supabase PostgREST filter strings (.or, .ilike, .like).
// The only characters SQL LIKE treats as wildcards are %, _ and \ (the
// default escape). Commas + parens are ONLY meaningful on the .or()
// separator boundary, not inside a value, and stars are a PostgREST URL
// alias for % — so escaping them had no benefit but produced broken
// matches for legitimate values like "Smith (executor)". Narrowed to
// the three chars that actually matter.
export function escapeFilterValue(val) {
  if (!val) return "";
  return String(val).replace(/[%_\\]/g, c => "\\" + c);
}

// Quote a value for PostgREST's or=/and() filter grammar.
//
// escapeFilterValue() escapes LIKE wildcards; it does NOT make a value
// safe inside or=(...) or and(...). There, a comma ends the argument and
// parentheses nest, so an unquoted value containing either produces a
// malformed filter and PostgREST answers 400 — which the app's
// try/catch turns into an empty result rather than an error.
//
// This bites constantly now that property addresses carry city, state
// and zip: "7200 Bogley, District Heights, MD 20747" has two commas.
// Values are double-quoted, with embedded quotes and backslashes
// escaped, which is exactly what PostgREST expects.
export function pgrestQuote(val) {
  return '"' + String(val ?? "").replace(/(["\\])/g, "\\$1") + '"';
}

// Case-insensitive email equality. `_` in a raw .ilike pattern is a SQL
// LIKE wildcard, so emails like "john_doe@x.com" used to match
// "johnxdoe@x.com" too — a real collision when two users differ only by
// an underscore. Escape the value so every character is literal.
export function emailFilterValue(email) {
  return escapeFilterValue((email || "").trim().toLowerCase());
}
// Sanitize HTML for safe insertion into document.write() contexts
export function sanitizeForPrint(str) {
  if (!str) return "";
  return String(str).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
}

export const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

export const STATE_NAMES = {AL:"Alabama",AK:"Alaska",AZ:"Arizona",AR:"Arkansas",CA:"California",CO:"Colorado",CT:"Connecticut",DE:"Delaware",DC:"District of Columbia",FL:"Florida",GA:"Georgia",HI:"Hawaii",ID:"Idaho",IL:"Illinois",IN:"Indiana",IA:"Iowa",KS:"Kansas",KY:"Kentucky",LA:"Louisiana",ME:"Maine",MD:"Maryland",MA:"Massachusetts",MI:"Michigan",MN:"Minnesota",MS:"Mississippi",MO:"Missouri",MT:"Montana",NE:"Nebraska",NV:"Nevada",NH:"New Hampshire",NJ:"New Jersey",NM:"New Mexico",NY:"New York",NC:"North Carolina",ND:"North Dakota",OH:"Ohio",OK:"Oklahoma",OR:"Oregon",PA:"Pennsylvania",RI:"Rhode Island",SC:"South Carolina",SD:"South Dakota",TN:"Tennessee",TX:"Texas",UT:"Utah",VT:"Vermont",VA:"Virginia",WA:"Washington",WV:"West Virginia",WI:"Wisconsin",WY:"Wyoming"};

// ============ PROPERTY SETUP WIZARD — shared step list ============
// Single source of truth for "which wizard steps apply to this property
// given its status + the caller's role". The wizard itself, the
// Properties Review page, and Tasks & Approvals all derive from this
// so a Skip in one place shows up as a pending task in another.
//
// `review` is intentionally excluded — it's a summary screen, never a
// skippable task. Callers that need it (e.g. the wizard's own step
// array) append it locally.
// Which licences a property is required to hold.
//
// Lead paint is a PRE-1978 rule, not post: the federal ban on lead-based
// paint took effect in 1978, so disclosure and Maryland's MDE
// registration apply to housing built BEFORE then. Gating it the other
// way round would flag exactly the wrong properties and miss every one
// that actually needs a certificate.
//
// An unknown year is its own answer. Treating NULL as pre-1978 flags the
// whole portfolio; treating it as post-1978 flags none. It returns
// "unknown" so the compliance report can ask for the year rather than
// quietly guess.
export const LEAD_PAINT_CUTOFF_YEAR = 1978;

export function requiredLicenses({ yearBuilt } = {}) {
  const out = [{ type: "rental_license", required: true }];
  const y = Number(yearBuilt);
  if (!yearBuilt || Number.isNaN(y)) {
    out.push({ type: "lead_paint", required: "unknown" });
  } else if (y < LEAD_PAINT_CUTOFF_YEAR) {
    out.push({ type: "lead_paint", required: true });
  }
  return out;
}

export function getWizardApplicableSteps({ propertyStatus, userRole, yearBuilt, allowedPages } = {}) {
  const s = ["property_details"];
  if (propertyStatus === "occupied") s.push("tenant_lease");
  s.push("utilities", "hoa");
  // The loan step follows Loans-module access, not a role. An employee whose
  // custom access includes the Loans module (allowedPages has "loans") records
  // loans too -- matching the Team & Roles checkboxes. When allowedPages is not
  // supplied (the review/task counters, which do not carry the viewer's menu)
  // fall back to the old admin/owner check so their totals are unchanged.
  const canLoan = Array.isArray(allowedPages)
    ? allowedPages.includes("loans")
    : (userRole === "admin" || userRole === "owner");
  if (canLoan) s.push("loan");
  // Every rental property needs a licence, so the wizard asks for one.
  // It had never asked: "rental_license" existed only as a TYPE in a
  // dropdown behind Properties -> a property -> Licenses tab -> add, and
  // the result was that all 41 rental properties had zero licences
  // recorded. A feature nothing prompts for does not get used.
  s.push("documents", "insurance", "property_tax", "rental_license");
  // Lead paint certificate: only for pre-1978 housing, and also when the
  // year is unknown -- because the wizard is exactly where that gets
  // filled in, and skipping the question is how the year stays unknown.
  if (!yearBuilt || Number(yearBuilt) < LEAD_PAINT_CUTOFF_YEAR) s.push("lead_paint");
  if (propertyStatus === "occupied") s.push("recurring_rent");
  return s;
}

export const WIZARD_STEP_LABELS = {
  property_details: "Property Details",
  tenant_lease: "Tenant & Lease",
  utilities: "Utilities",
  hoa: "HOA",
  loan: "Loan / Mortgage",
  documents: "Documents",
  insurance: "Insurance",
  property_tax: "Property Tax",
  rental_license: "Rental License",
  lead_paint: "Lead Paint Certificate",
  recurring_rent: "Recurring Rent",
};

export const WIZARD_STEP_ICONS = {
  property_details: "home",
  tenant_lease: "people",
  utilities: "bolt",
  hoa: "holiday_village",
  loan: "account_balance_wallet",
  documents: "description",
  insurance: "verified_user",
  property_tax: "receipt_long",
  rental_license: "badge",
  lead_paint: "science",
  recurring_rent: "event_repeat",
};

// ============ APPROVAL ROUTING ============
// Admin/owner see every approval queue row. Manager sees only the rows
// whose approver_email was snapshotted to them when the requester
// submitted (their assigned staff). Everyone else sees nothing. Team
// join requests are handled separately (admin-only) and don't flow
// through this helper.
// Who may decide a request: what the database's _request_guard_review
// (20261004252000) allows -- an admin, or the manager / pm it was routed to.
// An OWNER is not on that list: the page used to show owners Approve and the
// database then refused them (2026-10-13).
export function canReviewRequest({ userRole, userEmail, approverEmail }) {
  if (userRole === "admin") return true;
  if (userRole !== "manager" && userRole !== "pm") return false;
  if (!approverEmail || !userEmail) return false;
  return approverEmail.toLowerCase() === userEmail.toLowerCase();
}

// Counties / independent cities the portfolio operates in. Mirrors the
// FlipRadar active-counties registry (shared/counties.js) for the DMV,
// plus Richmond City, Baltimore City, and York County PA that are
// specific to Housify's portfolio.
//
// Totals: 1 DC + 11 MD + 14 VA + 1 PA = 27 jurisdictions.
//
// DC is not a county but is treated as a single jurisdiction. VA has
// both counties AND independent cities that each levy their own
// property tax, hence the "... City" entries sitting alongside
// "... County".
export const COUNTIES_BY_STATE = {
  DC: ["District of Columbia"],
  MD: [
    "Anne Arundel County",
    "Baltimore City",
    "Baltimore County",
    "Calvert County",
    "Charles County",
    "Frederick County",
    "Harford County",
    "Howard County",
    "Montgomery County",
    "Prince George's County",
    "St. Mary's County",
  ],
  VA: [
    "Alexandria City",
    "Arlington County",
    "Fairfax City",
    "Fairfax County",
    "Falls Church City",
    "Fauquier County",
    "Fredericksburg City",
    "Loudoun County",
    "Manassas City",
    "Manassas Park City",
    "Prince William County",
    "Richmond City",
    "Spotsylvania County",
    "Stafford County",
  ],
  PA: ["York County"],
};

// Property-tax billing schedule per jurisdiction. Keyed by
// "<county>|<state>". Each entry is an array of installments: month/day
// and a human label. For fiscal-year jurisdictions (Falls Church,
// Manassas Park) the label still maps to the calendar month/day the bill
// is due; year rollover happens in the bill generator.
//
// Sources (for audit): DC OTR, MD SDAT + individual county treasurers,
// VA per-locality tax admin pages, York County PA assessment office,
// Richmond City finance. See research notes in the PR description.
//
// These are DEFAULTS — a PM can edit a generated bill's due_date on any
// property if their specific jurisdiction shifts a date (not uncommon).
export const COUNTY_TAX_SCHEDULES = {
  // DC
  "District of Columbia|DC": [
    { label: "1st half (DC)",    month: 3, day: 31 },
    { label: "2nd half (DC)",    month: 9, day: 15 },
  ],

  // MD — the annual bill is issued 1 July and an LLC pays it in ONE
  // payment by 30 September. The semi-annual split (30 Sep + 31 Dec) is
  // an option for owner-occupied classes, not how these properties are
  // paid, and generating two halves meant every Maryland property showed
  // a second bill that is never paid.
  //
  // Sahil, 2026-09-12: "Maryland LLC need to pay all tax together."
  // Applies to every Maryland property.
  "Anne Arundel County|MD":      [{ label: "Annual (MD)", month: 9, day: 30 }],
  "Baltimore County|MD":         [{ label: "Annual (MD)", month: 9, day: 30 }],
  "Calvert County|MD":           [{ label: "Annual (MD)", month: 9, day: 30 }],
  "Charles County|MD":           [{ label: "Annual (MD)", month: 9, day: 30 }],
  "Frederick County|MD":         [{ label: "Annual (MD)", month: 9, day: 30 }],
  "Harford County|MD":           [{ label: "Annual (MD)", month: 9, day: 30 }],
  "Howard County|MD":            [{ label: "Annual (MD)", month: 9, day: 30 }],
  "Montgomery County|MD":        [{ label: "Annual (MD)", month: 9, day: 30 }],
  "Prince George's County|MD":   [{ label: "Annual (MD)", month: 9, day: 30 }],
  "St. Mary's County|MD":        [{ label: "Annual (MD)", month: 9, day: 30 }],
  "Baltimore City|MD":           [{ label: "Annual (MD)", month: 9, day: 30 }],


  // VA standard NoVA/surrounding counties: Jun 5 + Dec 5.
  "Loudoun County|VA":          [{ label: "1st half (VA)", month: 6, day: 5  }, { label: "2nd half (VA)", month: 12, day: 5  }],
  "Stafford County|VA":         [{ label: "1st half (VA)", month: 6, day: 5  }, { label: "2nd half (VA)", month: 12, day: 5  }],
  "Spotsylvania County|VA":     [{ label: "1st half (VA)", month: 6, day: 5  }, { label: "2nd half (VA)", month: 12, day: 5  }],
  "Fauquier County|VA":         [{ label: "1st half (VA)", month: 6, day: 5  }, { label: "2nd half (VA)", month: 12, day: 5  }],
  // Fredericksburg City — fiscal-year FY ending Jun 30, so 1st half is
  // Dec 5 of the prior calendar year and 2nd half Jun 5 of the tax year.
  "Fredericksburg City|VA":     [{ label: "1st half (VA)", month: 12, day: 5 }, { label: "2nd half (VA)", month: 6,  day: 5  }],
  // Fairfax County: Jul 28 (not Jun 5) + Dec 5. Fairfax City defaults
  // to same for now; user can adjust per property.
  "Fairfax County|VA":          [{ label: "1st half (VA)", month: 7, day: 28 }, { label: "2nd half (VA)", month: 12, day: 5  }],
  "Fairfax City|VA":            [{ label: "1st half (VA)", month: 7, day: 28 }, { label: "2nd half (VA)", month: 12, day: 5  }],
  // Arlington: Jun 15 + Oct 5 (unusual — not Dec 5).
  "Arlington County|VA":        [{ label: "1st half (VA)", month: 6, day: 15 }, { label: "2nd half (VA)", month: 10, day: 5  }],
  // Alexandria City: Jun 15 + Nov 15.
  "Alexandria City|VA":         [{ label: "1st half (VA)", month: 6, day: 15 }, { label: "2nd half (VA)", month: 11, day: 15 }],
  // Falls Church + Manassas Park: fiscal-year billing Dec 5 + Jun 5.
  "Falls Church City|VA":       [{ label: "1st half (VA)", month: 12, day: 5 }, { label: "2nd half (VA)", month: 6,  day: 5  }],
  "Manassas Park City|VA":      [{ label: "1st half (VA)", month: 12, day: 5 }, { label: "2nd half (VA)", month: 6,  day: 5  }],
  // Prince William County: Jul 15 + Dec 5 (calendar year).
  "Prince William County|VA":   [{ label: "1st half (VA)", month: 7, day: 15 }, { label: "2nd half (VA)", month: 12, day: 5  }],
  // Manassas City — fiscal-year like Manassas Park + Falls Church: Dec 5 + Jun 5.
  "Manassas City|VA":           [{ label: "1st half (VA)", month: 12, day: 5 }, { label: "2nd half (VA)", month: 6,  day: 5  }],
  // Richmond City: Jan 14 + Jun 14 — uniquely early for VA.
  "Richmond City|VA":           [{ label: "1st half (VA)", month: 1, day: 14 }, { label: "2nd half (VA)", month: 6, day: 14  }],

  // PA York County — 3 bills per year per property: county + municipal
  // (combined) + school district. All delinquent Dec 31. For tracking we
  // represent the two distinct bill cycles; the discount/face/penalty
  // windows are notes the user can eyeball via the records URL.
  "York County|PA": [
    { label: "County & Municipal (PA)", month: 4, day: 30  },   // face amount window
    { label: "School District (PA)",    month: 10, day: 31 },   // face amount window
  ],
};

// Look up a tax schedule tolerantly.
//
// COUNTY_TAX_SCHEDULES is keyed "Charles County|MD", but properties store
// the county as the user or the import typed it -- "Charles". A bare
// equality lookup therefore missed 66 of 79 properties with a county set
// (Anne Arundel, Charles, Harford, Howard, Prince George's), generated no
// tax bills for any of them, and reported them to the user as
// "out-of-area", which is not what happened.
//
// Ambiguity is NOT resolved by guessing. Maryland has both a "Baltimore
// County" and a "Baltimore City"; a bare "Baltimore" matches both, and
// picking one would silently file bills against the wrong jurisdiction's
// due dates. That returns { ambiguous } so the caller can say so.
export function findCountySchedule(county, state) {
  const st = String(state || "").trim().toUpperCase();
  const raw = String(county || "").trim().replace(/\s+/g, " ");
  if (!raw || !st) return { schedule: null, key: null, reason: "missing_input" };

  // Exact first -- the cheap, unambiguous case.
  const exact = COUNTY_TAX_SCHEDULES[raw + "|" + st];
  if (exact) return { schedule: exact, key: raw + "|" + st, reason: "exact" };

  // Otherwise compare on the bare name, with any County/City suffix
  // removed from BOTH sides, case-insensitively.
  const bare = (v) => v.replace(/\s+(County|City|Parish|Borough)$/i, "").toLowerCase();
  const want = bare(raw);
  const matches = Object.keys(COUNTY_TAX_SCHEDULES).filter(k => {
    const [name, keySt] = k.split("|");
    return keySt === st && bare(name) === want;
  });
  if (matches.length === 1) {
    return { schedule: COUNTY_TAX_SCHEDULES[matches[0]], key: matches[0], reason: "normalised" };
  }
  if (matches.length > 1) {
    return { schedule: null, key: null, reason: "ambiguous", candidates: matches };
  }
  return { schedule: null, key: null, reason: "no_schedule_for_jurisdiction" };
}


// Best-effort ZIP → {state, county} lookup for the DMV + Richmond + York PA
// operating area. Fed to tests/backfill-property-county.js for legacy rows;
// NOT authoritative — the wizard dropdown + validation is the source of
// truth going forward. Any ZIP we don't cover leaves county NULL and the
// wizard will demand it on the next edit.
export const ZIP_TO_COUNTY = {
  // ===== DC — 20001-20099 (residential only; skip gov-only 20500s) =====
  "20001": { state: "DC", county: "District of Columbia" }, "20002": { state: "DC", county: "District of Columbia" },
  "20003": { state: "DC", county: "District of Columbia" }, "20004": { state: "DC", county: "District of Columbia" },
  "20005": { state: "DC", county: "District of Columbia" }, "20007": { state: "DC", county: "District of Columbia" },
  "20008": { state: "DC", county: "District of Columbia" }, "20009": { state: "DC", county: "District of Columbia" },
  "20010": { state: "DC", county: "District of Columbia" }, "20011": { state: "DC", county: "District of Columbia" },
  "20012": { state: "DC", county: "District of Columbia" }, "20015": { state: "DC", county: "District of Columbia" },
  "20016": { state: "DC", county: "District of Columbia" }, "20017": { state: "DC", county: "District of Columbia" },
  "20018": { state: "DC", county: "District of Columbia" }, "20019": { state: "DC", county: "District of Columbia" },
  "20020": { state: "DC", county: "District of Columbia" }, "20024": { state: "DC", county: "District of Columbia" },
  "20032": { state: "DC", county: "District of Columbia" }, "20036": { state: "DC", county: "District of Columbia" },
  "20037": { state: "DC", county: "District of Columbia" },

  // ===== MD Montgomery County — 208xx, 209xx =====
  "20814": { state: "MD", county: "Montgomery County" }, "20815": { state: "MD", county: "Montgomery County" },
  "20816": { state: "MD", county: "Montgomery County" }, "20817": { state: "MD", county: "Montgomery County" },
  "20832": { state: "MD", county: "Montgomery County" }, "20833": { state: "MD", county: "Montgomery County" },
  "20850": { state: "MD", county: "Montgomery County" }, "20851": { state: "MD", county: "Montgomery County" },
  "20852": { state: "MD", county: "Montgomery County" }, "20853": { state: "MD", county: "Montgomery County" },
  "20854": { state: "MD", county: "Montgomery County" }, "20855": { state: "MD", county: "Montgomery County" },
  "20861": { state: "MD", county: "Montgomery County" }, "20862": { state: "MD", county: "Montgomery County" },
  "20866": { state: "MD", county: "Montgomery County" }, "20871": { state: "MD", county: "Montgomery County" },
  "20872": { state: "MD", county: "Montgomery County" }, "20874": { state: "MD", county: "Montgomery County" },
  "20876": { state: "MD", county: "Montgomery County" }, "20877": { state: "MD", county: "Montgomery County" },
  "20878": { state: "MD", county: "Montgomery County" }, "20879": { state: "MD", county: "Montgomery County" },
  "20882": { state: "MD", county: "Montgomery County" }, "20886": { state: "MD", county: "Montgomery County" },
  "20895": { state: "MD", county: "Montgomery County" }, "20896": { state: "MD", county: "Montgomery County" },
  "20901": { state: "MD", county: "Montgomery County" }, "20902": { state: "MD", county: "Montgomery County" },
  "20903": { state: "MD", county: "Montgomery County" }, "20904": { state: "MD", county: "Montgomery County" },
  "20905": { state: "MD", county: "Montgomery County" }, "20906": { state: "MD", county: "Montgomery County" },
  "20910": { state: "MD", county: "Montgomery County" }, "20912": { state: "MD", county: "Montgomery County" },

  // ===== MD Prince George's County — 207xx, parts of 206xx/208xx =====
  "20707": { state: "MD", county: "Prince George's County" }, "20708": { state: "MD", county: "Prince George's County" },
  "20710": { state: "MD", county: "Prince George's County" }, "20712": { state: "MD", county: "Prince George's County" },
  "20715": { state: "MD", county: "Prince George's County" }, "20716": { state: "MD", county: "Prince George's County" },
  "20720": { state: "MD", county: "Prince George's County" }, "20721": { state: "MD", county: "Prince George's County" },
  "20735": { state: "MD", county: "Prince George's County" }, "20737": { state: "MD", county: "Prince George's County" },
  "20740": { state: "MD", county: "Prince George's County" }, "20742": { state: "MD", county: "Prince George's County" },
  "20743": { state: "MD", county: "Prince George's County" }, "20744": { state: "MD", county: "Prince George's County" },
  "20745": { state: "MD", county: "Prince George's County" }, "20746": { state: "MD", county: "Prince George's County" },
  "20747": { state: "MD", county: "Prince George's County" }, "20748": { state: "MD", county: "Prince George's County" },
  "20762": { state: "MD", county: "Prince George's County" }, "20769": { state: "MD", county: "Prince George's County" },
  "20770": { state: "MD", county: "Prince George's County" }, "20772": { state: "MD", county: "Prince George's County" },
  "20774": { state: "MD", county: "Prince George's County" }, "20781": { state: "MD", county: "Prince George's County" },
  "20782": { state: "MD", county: "Prince George's County" }, "20783": { state: "MD", county: "Prince George's County" },
  "20784": { state: "MD", county: "Prince George's County" }, "20785": { state: "MD", county: "Prince George's County" },

  // ===== MD Charles / Frederick / Howard / Anne Arundel / Calvert / St. Mary's — partial =====
  "20601": { state: "MD", county: "Charles County" }, "20602": { state: "MD", county: "Charles County" },
  "20603": { state: "MD", county: "Charles County" }, "20640": { state: "MD", county: "Charles County" },
  "21701": { state: "MD", county: "Frederick County" }, "21702": { state: "MD", county: "Frederick County" },
  "21703": { state: "MD", county: "Frederick County" }, "21704": { state: "MD", county: "Frederick County" },
  "21709": { state: "MD", county: "Frederick County" }, "21754": { state: "MD", county: "Frederick County" },
  "21770": { state: "MD", county: "Frederick County" }, "21771": { state: "MD", county: "Frederick County" },
  "21774": { state: "MD", county: "Frederick County" }, "21793": { state: "MD", county: "Frederick County" },
  "21043": { state: "MD", county: "Howard County" }, "21044": { state: "MD", county: "Howard County" },
  "21045": { state: "MD", county: "Howard County" }, "21046": { state: "MD", county: "Howard County" },
  "21075": { state: "MD", county: "Howard County" },
  "21401": { state: "MD", county: "Anne Arundel County" }, "21403": { state: "MD", county: "Anne Arundel County" },
  "21409": { state: "MD", county: "Anne Arundel County" },
  "20678": { state: "MD", county: "Calvert County" }, "20657": { state: "MD", county: "Calvert County" },
  "20653": { state: "MD", county: "St. Mary's County" }, "20650": { state: "MD", county: "St. Mary's County" },

  // ===== MD Baltimore City (21201-21231) — independent of Baltimore County =====
  "21201": { state: "MD", county: "Baltimore City" }, "21202": { state: "MD", county: "Baltimore City" },
  "21205": { state: "MD", county: "Baltimore City" }, "21206": { state: "MD", county: "Baltimore City" },
  "21210": { state: "MD", county: "Baltimore City" }, "21211": { state: "MD", county: "Baltimore City" },
  "21212": { state: "MD", county: "Baltimore City" }, "21213": { state: "MD", county: "Baltimore City" },
  "21214": { state: "MD", county: "Baltimore City" }, "21215": { state: "MD", county: "Baltimore City" },
  "21216": { state: "MD", county: "Baltimore City" }, "21217": { state: "MD", county: "Baltimore City" },
  "21218": { state: "MD", county: "Baltimore City" }, "21223": { state: "MD", county: "Baltimore City" },
  "21224": { state: "MD", county: "Baltimore City" }, "21225": { state: "MD", county: "Baltimore City" },
  "21226": { state: "MD", county: "Baltimore City" }, "21229": { state: "MD", county: "Baltimore City" },
  "21230": { state: "MD", county: "Baltimore City" }, "21231": { state: "MD", county: "Baltimore City" },

  // ===== MD Baltimore County — surrounds the City; primary residential ZIPs =====
  "21204": { state: "MD", county: "Baltimore County" }, "21207": { state: "MD", county: "Baltimore County" },
  "21208": { state: "MD", county: "Baltimore County" }, "21209": { state: "MD", county: "Baltimore County" },
  "21219": { state: "MD", county: "Baltimore County" }, "21220": { state: "MD", county: "Baltimore County" },
  "21221": { state: "MD", county: "Baltimore County" }, "21222": { state: "MD", county: "Baltimore County" },
  "21227": { state: "MD", county: "Baltimore County" }, "21228": { state: "MD", county: "Baltimore County" },
  "21234": { state: "MD", county: "Baltimore County" }, "21236": { state: "MD", county: "Baltimore County" },
  "21237": { state: "MD", county: "Baltimore County" }, "21244": { state: "MD", county: "Baltimore County" },

  // ===== MD Harford County — 210xx–215xx =====
  "21001": { state: "MD", county: "Harford County" }, "21009": { state: "MD", county: "Harford County" },
  "21014": { state: "MD", county: "Harford County" }, "21015": { state: "MD", county: "Harford County" },
  "21017": { state: "MD", county: "Harford County" }, "21028": { state: "MD", county: "Harford County" },
  "21034": { state: "MD", county: "Harford County" }, "21040": { state: "MD", county: "Harford County" },
  "21050": { state: "MD", county: "Harford County" }, "21078": { state: "MD", county: "Harford County" },
  "21084": { state: "MD", county: "Harford County" }, "21085": { state: "MD", county: "Harford County" },
  "21154": { state: "MD", county: "Harford County" }, "21160": { state: "MD", county: "Harford County" },

  // ===== VA Fredericksburg City — 22401-22408 (some are surrounding counties, keep to city proper) =====
  "22401": { state: "VA", county: "Fredericksburg City" }, "22408": { state: "VA", county: "Fredericksburg City" },

  // ===== VA Arlington — 222xx =====
  "22201": { state: "VA", county: "Arlington County" }, "22202": { state: "VA", county: "Arlington County" },
  "22203": { state: "VA", county: "Arlington County" }, "22204": { state: "VA", county: "Arlington County" },
  "22205": { state: "VA", county: "Arlington County" }, "22206": { state: "VA", county: "Arlington County" },
  "22207": { state: "VA", county: "Arlington County" }, "22209": { state: "VA", county: "Arlington County" },
  "22213": { state: "VA", county: "Arlington County" }, "22214": { state: "VA", county: "Arlington County" },

  // ===== VA Fairfax County incl. independent Fairfax City + Falls Church City =====
  "22003": { state: "VA", county: "Fairfax County" }, "22015": { state: "VA", county: "Fairfax County" },
  "22027": { state: "VA", county: "Fairfax County" }, "22030": { state: "VA", county: "Fairfax City" },
  "22031": { state: "VA", county: "Fairfax County" }, "22032": { state: "VA", county: "Fairfax County" },
  "22033": { state: "VA", county: "Fairfax County" }, "22039": { state: "VA", county: "Fairfax County" },
  "22041": { state: "VA", county: "Fairfax County" }, "22042": { state: "VA", county: "Fairfax County" },
  "22043": { state: "VA", county: "Fairfax County" }, "22044": { state: "VA", county: "Falls Church City" },
  "22046": { state: "VA", county: "Falls Church City" }, "22060": { state: "VA", county: "Fairfax County" },
  "22066": { state: "VA", county: "Fairfax County" }, "22079": { state: "VA", county: "Fairfax County" },
  "22101": { state: "VA", county: "Fairfax County" }, "22102": { state: "VA", county: "Fairfax County" },
  "22124": { state: "VA", county: "Fairfax County" }, "22150": { state: "VA", county: "Fairfax County" },
  "22151": { state: "VA", county: "Fairfax County" }, "22152": { state: "VA", county: "Fairfax County" },
  "22153": { state: "VA", county: "Fairfax County" }, "22180": { state: "VA", county: "Fairfax County" },
  "22181": { state: "VA", county: "Fairfax County" }, "22182": { state: "VA", county: "Fairfax County" },
  "22303": { state: "VA", county: "Fairfax County" }, "22306": { state: "VA", county: "Fairfax County" },
  "22307": { state: "VA", county: "Fairfax County" }, "22309": { state: "VA", county: "Fairfax County" },
  "22310": { state: "VA", county: "Fairfax County" }, "22311": { state: "VA", county: "Fairfax County" },
  "22312": { state: "VA", county: "Fairfax County" }, "22315": { state: "VA", county: "Fairfax County" },

  // ===== VA Alexandria City — 223xx =====
  "22301": { state: "VA", county: "Alexandria City" }, "22302": { state: "VA", county: "Alexandria City" },
  "22304": { state: "VA", county: "Alexandria City" }, "22305": { state: "VA", county: "Alexandria City" },
  "22314": { state: "VA", county: "Alexandria City" },

  // ===== VA Loudoun — 201xx (Fairfax vs Loudoun overlap noted inline) =====
  "20105": { state: "VA", county: "Loudoun County" }, "20120": { state: "VA", county: "Fairfax County" },
  "20147": { state: "VA", county: "Loudoun County" }, "20148": { state: "VA", county: "Loudoun County" },
  "20151": { state: "VA", county: "Fairfax County" }, "20152": { state: "VA", county: "Loudoun County" },
  "20164": { state: "VA", county: "Loudoun County" }, "20165": { state: "VA", county: "Loudoun County" },
  "20166": { state: "VA", county: "Loudoun County" }, "20170": { state: "VA", county: "Fairfax County" },
  "20171": { state: "VA", county: "Fairfax County" }, "20175": { state: "VA", county: "Loudoun County" },
  "20176": { state: "VA", county: "Loudoun County" }, "20180": { state: "VA", county: "Loudoun County" },
  "20190": { state: "VA", county: "Fairfax County" }, "20191": { state: "VA", county: "Fairfax County" },
  "20194": { state: "VA", county: "Fairfax County" },

  // ===== VA Prince William / Manassas independent cities =====
  "20109": { state: "VA", county: "Prince William County" }, "20110": { state: "VA", county: "Manassas City" },
  "20111": { state: "VA", county: "Manassas City" }, "20112": { state: "VA", county: "Prince William County" },
  "22191": { state: "VA", county: "Prince William County" }, "22192": { state: "VA", county: "Prince William County" },
  "22193": { state: "VA", county: "Prince William County" },

  // ===== VA Stafford / Spotsylvania / Fauquier — partial =====
  "22554": { state: "VA", county: "Stafford County" }, "22556": { state: "VA", county: "Stafford County" },
  "22407": { state: "VA", county: "Spotsylvania County" }, "22551": { state: "VA", county: "Spotsylvania County" },
  "20186": { state: "VA", county: "Fauquier County" }, "20187": { state: "VA", county: "Fauquier County" },

  // ===== VA Richmond City — 232xx =====
  "23220": { state: "VA", county: "Richmond City" }, "23221": { state: "VA", county: "Richmond City" },
  "23222": { state: "VA", county: "Richmond City" }, "23223": { state: "VA", county: "Richmond City" },
  "23224": { state: "VA", county: "Richmond City" }, "23225": { state: "VA", county: "Richmond City" },
  "23226": { state: "VA", county: "Richmond City" }, "23227": { state: "VA", county: "Richmond City" },
  "23230": { state: "VA", county: "Richmond City" }, "23231": { state: "VA", county: "Richmond City" },
  "23233": { state: "VA", county: "Richmond City" }, "23234": { state: "VA", county: "Richmond City" },

  // ===== PA York County — 173xx–174xx =====
  "17313": { state: "PA", county: "York County" }, "17315": { state: "PA", county: "York County" },
  "17331": { state: "PA", county: "York County" }, "17339": { state: "PA", county: "York County" },
  "17340": { state: "PA", county: "York County" }, "17349": { state: "PA", county: "York County" },
  "17356": { state: "PA", county: "York County" }, "17401": { state: "PA", county: "York County" },
  "17402": { state: "PA", county: "York County" }, "17403": { state: "PA", county: "York County" },
  "17404": { state: "PA", county: "York County" }, "17406": { state: "PA", county: "York County" },
  "17407": { state: "PA", county: "York County" }, "17408": { state: "PA", county: "York County" },
};

export const statusColors = {
  occupied: "bg-positive-100 text-positive-700",
  vacant: "bg-caution-100 text-caution-700",
  maintenance: "bg-danger-100 text-danger-700",
  "notice given": "bg-notice-100 text-notice-700",
  active: "bg-positive-100 text-positive-700",
  notice: "bg-notice-100 text-notice-700",
  open: "bg-info-100 text-info-700",
  in_progress: "bg-highlight-100 text-highlight-700",
  completed: "bg-neutral-100 text-neutral-500",
  paid: "bg-positive-100 text-positive-700",
  partial: "bg-caution-100 text-caution-700",
  unpaid: "bg-danger-100 text-danger-700",
  pending: "bg-caution-100 text-caution-700",
  // A Stripe payment recorded but not yet in a booked bank deposit.
  awaiting_deposit: "bg-caution-100 text-caution-700",
  approved: "bg-positive-100 text-positive-700",
  eviction: "bg-danger-100 text-danger-700",
};

export const priorityColors = {
  emergency: "bg-danger-500 text-white",
  normal: "bg-info-100 text-info-700",
  low: "bg-neutral-100 text-neutral-500",
};

// A tenant with a live lease. The column holds TWO spellings for the same
// thing -- "active" from the tenant form's default, "current" from the
// Tenants page and the bulk importer -- and 46 rows say one while 28 say
// the other. Every `.eq("lease_status", "active")` therefore dropped a
// third of the tenants silently: late fees were never applied to them,
// rent posting did not list them, inspections could not find them.
//
// Use this everywhere instead of a single literal. The real fix is to
// settle on one word and migrate, which is a data decision.
export const ACTIVE_LEASE = ["active", "current"];

// A tenant who still LIVES at the property: active, or on notice (has given
// or been served notice but not yet moved out). 'active' is the one canonical
// word for a live tenancy -- the DB normalises 'current' to it (migration
// 20260928020000) -- and 'current' stays listed only so a stale row or an
// old cached bundle is not dropped.
export const LIVE_TENANCY = [...ACTIVE_LEASE, "notice"];
// An archived record never lives there, whatever its status says (Sahil,
// 2026-10-26: archived duplicates left 'active' showed as a second current
// tenant on the property page).
export const isLiveTenancy = (t) => !!t && !t.archived_at && LIVE_TENANCY.includes(String(t.lease_status || "").toLowerCase());
// A property someone lives in: occupied, or occupied with notice given
// ("notice"/"notice given" -- both written by older code). The dashboard
// counted only "occupied", so a property on notice was neither occupied
// nor vacant (audit FE-33).
export const OCCUPIED_PROPERTY = ["occupied", "notice", "notice given"];
export const isOccupiedProperty = (p) => OCCUPIED_PROPERTY.includes(String((p && p.status) || "").toLowerCase());
// The statuses a tenant can hold (the database's CHECK; it normalises the
// old "current", "expired" and "inactive"). Filters and forms offer these.
export const TENANT_STATUSES = ["active", "notice", "past", "review", "pending"];

// Compare two address strings for identity.
//
// `properties.address` is DERIVED -- a trigger composes it from the
// address components, so it is normalised. `tenants.property`,
// `leases.property` and `work_orders.property_address` are free text set
// at creation or import, and nothing normalises them. Joining the two
// with `===` therefore fails on a difference no human would call a
// difference: a trailing space on "13431 Marble Rock Dr, Chantilly, VA
// 20151 " made the Rent Roll print VACANT for an occupied unit.
//
// Deliberately only normalises case, surrounding and repeated
// whitespace. It does NOT strip unit designators: "100 Oak Street, Unit
// A" and "100 Oak Street" are different places, and collapsing them
// would attach Unit A's tenant to the building -- and with several units
// matching, silently show the wrong tenant's name and rent.
export const normalizeAddress = (v) =>
  String(v == null ? "" : v).trim().replace(/\s+/g, " ").toLowerCase();
export const sameAddress = (a, b) => {
  const x = normalizeAddress(a);
  return x !== "" && x === normalizeAddress(b);
};

// The short, human label for a property address.
//
// Addresses are stored as "line1, line2, city, STATE ZIP", where line2 is
// the unit and is often absent. Twenty-two call sites each did
// `address.split(",")[0]`, which keeps line1 and therefore DROPS THE UNIT:
// "353 Gatewater Ct, B, Glen Burnie, MD 21061" displayed as
// "353 Gatewater Ct", and "1865 Dutch Village, J-290, Hyattsville, MD
// 20785" as "1865 Dutch Village". Twelve properties show a label that is
// not their identity, and two units in one building would read the same.
// Sahil: "why does the addresses so simple. why are they not unique".
//
// So drop the city and state/zip from the END rather than keeping only the
// first segment. What is left is line1 plus the unit.
//
// Two details the naive version gets wrong:
//  * Some line1 values already carry the unit ("2010 Alice Ave #104") and
//    line2 repeats it ("104"), which would render "2010 Alice Ave #104 104".
//    A line2 already present in line1 is dropped.
//  * A handful of rows are malformed -- line1 itself holds a full address,
//    so the string has six segments. Keeping only what precedes the first
//    city-looking tail is impossible to do reliably there, so those fall
//    back to the first segment, which is what they showed before.
// Ledger descriptions come straight from the bank feed -- e.g. "Deposit Cash App
// DES:Anish Gupt ID:T39HXGKF855V7K1 INDN:Anish Gupt CO ID:XXXXX29876 PPD" -- and
// the same raw string is echoed as the line's memo, so the account ledger printed
// it TWICE. Reduce it to the human part plus the payer name (QuickBooks-style);
// anything without the bank/JE pattern passes through untouched.
export function cleanLedgerDesc(desc) {
  let s = String(desc == null ? "" : desc).trim();
  s = s.replace(/^Journal Entry\s*#?\d+\s*[-\u2013:]?\s*/i, "");
  const m = s.match(/^(.*?)\s+(?:DES:|ID:[A-Z0-9X]|INDN:|CO ID:)/i);
  if (m) {
    const head = m[1].replace(/^Deposit\s+/i, "").trim();
    const payer = (s.match(/INDN:\s*([A-Za-z][\w .,'&\/-]{1,40}?)(?:\s+CO ID:|\s+PPD\b|\s+WEB\b|\s+CCD\b|\s*$)/i) || [])[1];
    s = (head || "Payment") + (payer ? " \u2014 " + payer.replace(/\s+/g, " ").trim() : "");
  }
  return s.trim() || String(desc == null ? "" : desc).trim();
}

export function propertyLabel(address) {
  if (!address || typeof address !== "string") return "";
  const parts = address.split(",").map(s => s.trim()).filter(Boolean);
  if (parts.length === 0) return "";
  // "Overhead", or a bare street with no city -- nothing to trim.
  if (parts.length <= 2) return parts[0];
  // The tail is city + "STATE ZIP". Anything left over beyond one unit
  // segment means the address is malformed; don't guess.
  const head = parts.slice(0, parts.length - 2);
  if (head.length > 2) return parts[0];
  const [line1, line2] = head;
  if (!line2) return line1;
  // A unit already spelled out in line1 must not be repeated.
  const bare = line2.replace(/^#/, "");
  if (new RegExp(`(^|[\\s#])${bare.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}$`, "i").test(line1)) return line1;
  return `${line1} ${line2}`;
}

// Compose a property address from its parts -- a faithful port of the
// database's compute_property_address(), which the sync_addr_ins/upd triggers
// call and which therefore OWNS properties.address.
//
// This exists because the client needs the value before a row is written: to
// pre-check for a duplicate address, and to stamp a document uploaded
// mid-wizard. It must agree with the database to the character, because a
// document stamped with a near-miss address is invisible on the property page.
//
// The SQL it mirrors:
//   TRIM(CONCAT_WS(', ',
//     NULLIF(TRIM(line1),''), NULLIF(TRIM(line2),''), NULLIF(TRIM(city),''),
//     NULLIF(TRIM(CONCAT_WS(' ', NULLIF(TRIM(state),''), NULLIF(TRIM(zip),''))),'')))
//
// The version this replaces was [line1, line2, city, state + " " + zip]
// .filter(Boolean).join(", ") -- which trimmed nothing, so a trailing space in
// city survived into the address, and whose state+zip segment was the string
// " " when both were blank. " " passes filter(Boolean), so a property with no
// state or zip gained a stray ", " on the end.
//
// tests/address-formula.test.js asserts this against the cases the database
// function was run on directly.
export function composePropertyAddress({ address_line_1, address_line_2, city, state, zip } = {}) {
  const trimmed = (v) => String(v ?? "").trim();
  const nz = (v) => trimmed(v) || null;               // NULLIF(TRIM(x),'')
  const stateZip = nz([nz(state), nz(zip)].filter(Boolean).join(" "));
  return [nz(address_line_1), nz(address_line_2), nz(city), stateZip]
    .filter(Boolean)                                   // CONCAT_WS skips nulls
    .join(", ")
    .trim();
}

// ---- UPLOAD CHECK (audit LIFE-15) ----
// One check for every upload: size, type, and what the file's first bytes
// say it is (a renamed .exe claims "image/png" in its type, not its bytes).
// Returns an error message, or null when the file may go up. Fails CLOSED:
// a file that cannot be read is refused, not waved through.
//   kind: "doc"   PDF, images, Word/Excel/zip-based Office, plain text/CSV
//         "image" JPEG, PNG, GIF, WebP, HEIC
//         "pdf"   PDF only
const UPLOAD_MAGIC = {
  pdf: ["25504446"],                                   // %PDF
  png: ["89504e47"], jpeg: ["ffd8ff"], gif: ["47494638"],
  webp: ["52494646"],                                  // RIFF....WEBP (checked below)
  zip: ["504b0304"], ole: ["d0cf11e0"],                // docx/xlsx, doc/xls
};
function hexOf(bytes, n) { return Array.from(bytes.slice(0, n)).map(b => b.toString(16).padStart(2, "0")).join(""); }
export async function validateUploadFile(file, { kind = "doc", maxMB = 25 } = {}) {
  if (!file) return "Choose a file first.";
  if (file.size > maxMB * 1024 * 1024) return `The file must be under ${maxMB} MB.`;
  if (file.size === 0) return "The file is empty.";
  let head;
  try { head = new Uint8Array(await file.slice(0, 16).arrayBuffer()); }
  catch (_e) { return "The file could not be read. Try again, or choose another file."; }
  const hex = hexOf(head, 12);
  const is = (list) => list.some(m => hex.startsWith(m));
  const isWebp = is(UPLOAD_MAGIC.webp) && hexOf(head.slice(8), 4) === "57454250";
  const isHeic = hexOf(head.slice(4), 4) === "66747970";    // ....ftyp (HEIC/HEIF from iPhones)
  const isImage = is(UPLOAD_MAGIC.png) || is(UPLOAD_MAGIC.jpeg) || is(UPLOAD_MAGIC.gif) || isWebp || isHeic;
  if (kind === "pdf") return is(UPLOAD_MAGIC.pdf) ? null : "That is not a PDF file.";
  if (kind === "image") return isImage ? null : "That is not a photo (JPEG, PNG, GIF, WebP or HEIC).";
  if (is(UPLOAD_MAGIC.pdf) || isImage || is(UPLOAD_MAGIC.zip) || is(UPLOAD_MAGIC.ole)) return null;
  // Plain text / CSV: no NUL bytes and almost all printable in the first 512.
  // text/plain and text/csv only: HTML, JavaScript and the like are text too.
  const okType = ["text/plain", "text/csv"].includes(file.type || "") || (!file.type && /\.(txt|csv)$/i.test(file.name || ""));
  if (!okType) return "This kind of file is not accepted. Accepted: PDF, photos, Word, Excel, text and CSV.";
  let sniff;
  try { sniff = new Uint8Array(await file.slice(0, Math.min(512, file.size)).arrayBuffer()); }
  catch (_e) { return "The file could not be read. Try again, or choose another file."; }
  let bad = 0;
  for (const b of sniff) { if (b === 0) return "That file is not plain text."; if (!(b === 9 || b === 10 || b === 13 || (b >= 32 && b <= 126) || b >= 128)) bad++; }
  return bad / sniff.length < 0.05 ? null : "That file is not plain text.";
}

// One way to put names and addresses in order everywhere a list is shown:
// case-insensitive, and digit runs compared by value ("9 Oak St" before
// "10 Oak St", "Unit 2" before "Unit 10"). Lists that come from the database
// with .order() are fine as they are; this is for lists merged or derived
// in the browser (2026-10-06: 14 dropdowns and lists were in no order).
export const compareText = (a, b) => String(a ?? "").localeCompare(String(b ?? ""), undefined, { numeric: true, sensitivity: "base" });
export const byField = (field) => (a, b) => compareText(a?.[field], b?.[field]);
